import { useState, useEffect, useMemo } from 'react';
import { getSupabaseClient } from './supabase';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables in subscription-utils');
}

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'unpaid' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'all';

export interface UserSubscription {
  subscription_status: SubscriptionStatus | null;
  current_period_end: number | string | null;
  price_id?: string | null;
  cancel_at_period_end?: boolean | null;
}

// Fetch from consolidated secure view: stripe_user_subscriptions (security_invoker)
export async function getUserSubscription(_userId: string): Promise<UserSubscription | null> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('stripe_user_subscriptions')
    .select('*')
    .maybeSingle();

  if (error) {
    if (error.code === 'PGRST116') { // No rows returned
      return null;
    }
    console.error('Error fetching subscription:', error);
    throw error;
  }

  return data as any;
}

export async function hasActiveSubscription(userId: string): Promise<boolean> {
  const subscription = await getUserSubscription(userId);
  if (!subscription) return false;

  const now = new Date();
  // current_period_end can be epoch seconds (number) or ISO string; normalize
  const periodEnd = subscription.current_period_end
    ? (typeof subscription.current_period_end === 'number'
        ? new Date(subscription.current_period_end * 1000)
        : new Date(subscription.current_period_end))
    : null;
  
  return (
    (subscription.subscription_status === 'active' || subscription.subscription_status === 'trialing') &&
    (!periodEnd || periodEnd > now) &&
    !subscription.cancel_at_period_end
  );
}

export async function getCheckoutUrl(priceId: string, userId: string): Promise<string> {
  const supabase = getSupabaseClient();

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch('/api/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      priceId,
      userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create checkout session');
  }

  const { url } = await response.json();
  return url;
}

export async function getBillingPortalUrl(userId: string): Promise<string> {
  const supabase = getSupabaseClient();

  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch('/api/create-billing-portal-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create billing portal session');
  }

  const { url } = await response.json();
  return url;
}

// Simple auth hook for subscription utils
function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const supabase = getSupabaseClient();

    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
      } catch (error) {
        console.error('Error getting user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, isLoading };
}

// Hook to check if user has an active subscription
export function useSubscription() {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const supabase = getSupabaseClient();

    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        const sub = await getUserSubscription(user.id);
        setSubscription(sub);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch subscription'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();

    // Set up real-time subscription
    const subscriptionChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stripe_subscriptions',
        },
        (payload: any) => {
          // Re-fetch subscription data when changes occur
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
    };
  }, [user?.id]);

  const isPro = useMemo(() => {
    if (!subscription) return false;
    const now = new Date();
    const periodEnd = subscription.current_period_end ? new Date(subscription.current_period_end) : null;

    return (
      (subscription.subscription_status === 'active' || subscription.subscription_status === 'trialing') &&
      (!periodEnd || periodEnd > now) &&
      !subscription.cancel_at_period_end
    );
  }, [subscription]);

  return {
    subscription,
    isPro,
    isLoading,
    error,
  };
}
